<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUser } from '@/composables/useUser'
import { menuItems } from '@/constants/menu-items'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const router = useRouter()
const route = useRoute()
const showVerificationModal = ref(false)

//  User verification state & dashboard detection
const { user } = useUser()
const userName = computed(() => user.value?.fullName || 'کاربر')
const isProfileCompleted = computed(() => user.value?.status === 'VERIFIED')

const isDashboard = computed(() => route.path === '/profile/dashboard')
const isBankPages = computed(() => route.path.includes('bank') || route.path.includes('destination-currency-accounts'))

const handleMenuClick = (menuLink: string) => {
  const protectedMenus = ['/profile/offers', '/profile/transactions', '/profile/bank-accounts']
  const isProtected = protectedMenus.some((protectedMenu) => menuLink.includes(protectedMenu))

  if (!isProfileCompleted.value && isProtected) {
    showVerificationModal.value = true
    return
  }

  router.push(menuLink)
  emit('update:modelValue', false)
}

const handleActionClick = () => {
  if (!isProfileCompleted.value) {
    router.push('/profile/register-account')
    emit('update:modelValue', false)
  } else {
    router.push('/profile/submit-request')
    emit('update:modelValue', false)
  }
}

const handleModalNavigate = () => {
  router.push('/profile/register-account')
  emit('update:modelValue', false)
}

const handleBankClick = () => {
  router.push('/profile/bank-accounts/new')
  emit('update:modelValue', false)
}

const handleModalClose = () => {
  showVerificationModal.value = false
}
</script>

<template>
  <Modal
    :model-value="props.modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
    position="top"
    :closable="false"
    v-slot="{ close }"
  >
    <div>
      <!-- 👉 Header & User info   -->
      <div class="flex justify-between items-center border-b border-[#eaeef5] pb-4">
        <div class="flex items-center gap-1">
          <img @click="close" src="/svg/close-icon.svg" alt="close-icon" />
          <span class="text-[13px] text-[#2d5396] font-medium">منو</span>
        </div>
        <img class="w-[80px] h-[30px]" src="/svg/payjib-logo-mobile.svg" />
      </div>
      <div class="flex items-center gap-3 my-6">
        <img src="/svg/emoji.svg" width="24" alt="warning-icon" />
        <div>{{ userName }}</div>
      </div>
      <!-- 👉 Menu list -->
      <div
        v-for="(menu, index) in menuItems"
        :key="`menu-item-${index}`"
        class="d-flex items-center"
        :class="[
          index === 0
            ? 'border-b  mb-2 border-[#d7d7d8]  h-[57.45] py-2 pb-4'
            : index === 5
              ? 'mb-0 pb-0  h-[40.72px] py-2'
              : 'mb-5  h-[40.72px] py-2',
        ]"
      >
        <div
          @click="handleMenuClick(menu.link)"
          class="flex items-center gap-3 font-medium cursor-pointer"
        >
          <img :src="menu.icon_url" width="24" alt="menu-icon" />
          {{ menu.title }}
        </div>
      </div>
      <!-- 👉 Conditional action button / message -->
      <div
        v-if="!isDashboard && isProfileCompleted && !isBankPages"
        class="p-[18px] text-center text-[#2d5396] font-medium mt-2 mb-4"
      >
        برای ثبت درخواست خرید یا فروش از طریق منو وارد داشبورد شوید
      </div>
      <Button
        v-else-if="isBankPages"
        label="افزودن حساب جدید"
        class="w-full"
        :class="isProfileCompleted && isProfileCompleted ? 'mt-6 mb-4' : 'mt-2'"
        :loading="false"
        :disabled="false"
        @click="handleBankClick"
      />
      <Button
        v-else
        :label="isProfileCompleted ? 'ثبت درخواست جدید' : 'تکمیل حساب کاربری'"
        class="w-full"
        :class="isProfileCompleted ? 'mt-6 mb-4' : 'mt-2'"
        :loading="false"
        :disabled="false"
        @click="handleActionClick"
      />
    </div>
  </Modal>
  <!-- 👉 Verification Modal -->
  <ProfileVerificationModal
    v-model="showVerificationModal"
    @close="handleModalClose"
    @navigate="handleModalNavigate"
    class="z-[2325535345]"
  />
</template>
